import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { Schema } from 'effect';
import { Effect } from 'effect';

import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { orderConflictRetryPolicy } from '../../../service/api/conflictRetry';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import type { Paths } from '@vs-shop/schemas/openapi/paths';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/orders/{tokenValue}/items';
const HTTP_METHOD = 'post';

type RequestBodyOf<Op> = Op extends { requestBody: { content: infer C } } ? C[keyof C] : never;

type AddOrderItemBody = RequestBodyOf<Paths[typeof ENDPOINT][typeof HTTP_METHOD]>;

export type MakeAddProductVariantToOrderDeps<OShowType> = {
  readonly orderShow: Schema.Codec<OShowType, unknown>;
};

export const makeAddProductVariantToOrder = <OShowType>(
  deps: MakeAddProductVariantToOrderDeps<OShowType>
) => {
  const AddProductVariantError = createBackendApiError({
    domain: 'Order',
    endpoint: 'AddProductVariant',
    path: ENDPOINT,
    identifierField: 'orderToken',
  } as const);

  const postOrderItemFetch = (tokenValue: string, data: AddOrderItemBody) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json', 'application/json')
        .fetch({ params: { tokenValue }, body: data });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'order.token': tokenValue,
          'product.variant': data.productVariant,
          'product.quantity': String(data.quantity),
        },
      })
    );

  const addProductVariantToOrder = (tokenValue: string, data: AddOrderItemBody) =>
    postOrderItemFetch(tokenValue, data).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) => schemaBodyJsonClean(deps.orderShow)(response),
          404: (response) =>
            Effect.fail(
              new AddProductVariantError.NotFoundError({ orderToken: tokenValue, response })
            ),
          422: AddProductVariantError.createViolationError(tokenValue),
          orElse: (response) =>
            Effect.fail(
              new AddProductVariantError.ApiRequestError({ orderToken: tokenValue, response })
            ),
        })
      ),
      Effect.retry(orderConflictRetryPolicy),
      Effect.tap(() =>
        logApiOperation(
          ApiModule.OrderItemAdd,
          'Debug',
          `Successfully added product variant ${data.productVariant} (qty: ${String(data.quantity)}) to order ${tokenValue}`
        )
      ),
      Effect.withSpan('api.orderItem.add', {
        kind: 'internal',
        attributes: {
          'order.token': tokenValue,
          'product.variant': data.productVariant,
          'product.quantity': String(data.quantity),
        },
      })
    );

  return { addProductVariantToOrder, AddProductVariantError };
};
